import React, { useCallback, useContext } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from 'ncoded-component-library';
import HintMessage from 'components/HintMessage';
import AgreementFieldsPreview from 'components/AgreementFieldsPreview';
import AgreementPageLayout from 'components/AgreementPageLayout';
import useAgreementTemplateQuery from 'hooks/useAgreementTemplateQuery';
import { getIsAgreementTemplateActive } from 'agreementUtils';
import ListingContext from '../../providers/Listing/Listing.context';
import useRemoveAgreement from '../../hooks/useRemoveAgreement';
import {
  AgreementPageLocationState,
  getAgreementPageBackPath,
  getAgreementSetupRoute,
} from '../../utils';

import './AgreementView.styles.scss';
import './AgreementView.styles.responsive.scss';

const AgreementViewPage: React.FC = () => {
  const {
    areActionsDisabled,
    listing: { _id: listingId, isNativeEsignEnabled },
    dealRoom: { _id: dealRoomId, name: dealName },
  } = useContext(ListingContext);

  const navigate = useNavigate();
  const { pathname, state } = useLocation();

  const { t } = useTranslation();

  const backPath = getAgreementPageBackPath(state);

  const { data: template, isLoading } = useAgreementTemplateQuery(
    listingId,
    !!isNativeEsignEnabled,
  );

  const isActive = getIsAgreementTemplateActive(template);
  const { currentVersion } = template || {};

  const goBack = useCallback(() => navigate(backPath), [backPath, navigate]);

  const onRemove = useRemoveAgreement(goBack);

  if (!isNativeEsignEnabled) return <Navigate to={backPath} replace />;

  const renderContent = () => {
    if (isLoading) return <Loader />;

    if (!isActive) return <HintMessage message={t('youDontHaveNda')} />;

    return (
      <AgreementFieldsPreview
        className="agreement-view__preview"
        fileUrl={currentVersion.pdfUrl}
        fields={currentVersion.fields}
      />
    );
  };

  return (
    <AgreementPageLayout
      className="agreement-view"
      title={
        (isActive && currentVersion.originalFilename) ||
        t('nonDisclosureAgreement')
      }
      backPath={backPath}
      backLabel={dealName}
      actions={
        isActive && (
          <>
            <Button
              type="button"
              variant="outline"
              disabled={areActionsDisabled}
              onClick={() =>
                navigate(getAgreementSetupRoute(dealRoomId), {
                  state: { from: pathname } as AgreementPageLocationState,
                })
              }
            >
              {t('replace')}
            </Button>
            <Button
              type="button"
              className="agreement-view__remove"
              disabled={areActionsDisabled}
              onClick={onRemove}
            >
              {t('remove')}
            </Button>
          </>
        )
      }
    >
      {renderContent()}
    </AgreementPageLayout>
  );
};

export default AgreementViewPage;
